import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { ignoreNil } from "@obg/ngx.global";
import { RouterState, RouteSelector } from "@obg/ngx.router";
import { remove } from "lodash-es";
import { merge, Subscription } from "rxjs";
import { distinctUntilChanged, pairwise, startWith, tap } from "rxjs/operators";

import { ScrollToTopDetail } from "./scroll-to-top.model";

@Injectable({
	providedIn: "root",
})
export class ScrollToTopService {
	private data$$: Subscription | undefined;
	private scrollDetails: ScrollToTopDetail[] = [];

	constructor(private store: Store<RouterState>) {}

	register(scrollDetail: ScrollToTopDetail) {
		this.scrollDetails.push(scrollDetail);

		if (this.data$$) {
			return;
		}

		const routeChange$ = this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			distinctUntilChanged((prev, current) => prev?.pathName === current?.pathName),
			tap(() => this.triggerScroll(this.scrollDetails.filter((detail) => detail.routeChange)))
		);

		const queryChange$ = this.store.select(RouteSelector.getQueryParams).pipe(
			startWith(undefined),
			pairwise<Record<string, string> | undefined>(),
			tap(([prev, current]) => {
				const details = this.scrollDetails
					.filter((detail) => detail.queryChange && detail.queryParams)
					.filter((detail) =>
						detail.queryParams!.some((param) => (current && current[param]) !== (prev && prev[param]))
					);
				this.triggerScroll(details);
			})
		);

		this.data$$ = merge(routeChange$, queryChange$).subscribe();
	}

	unregister(id: string) {
		remove(this.scrollDetails, (element) => element.id === id);

		if (!this.scrollDetails.length && this.data$$) {
			this.data$$.unsubscribe();
			this.data$$ = undefined;
		}
	}

	triggerCommandScroll(duration?: number) {
		const details = this.scrollDetails.filter((detail) => detail.commandTrigger);
		this.triggerScroll(details, duration);
	}

	private triggerScroll(details: ScrollToTopDetail[], duration?: number) {
		for (const detail of details) {
			detail.element.scrollTo({ top: 0, duration });
		}
	}
}
